import Alert from '@mui/material/Alert';
import { History } from '@openx/components-core/lib/History/History.tsx';
import { DealDetailsTab } from '@openx/components-deal/lib/DealDetails/DealDetailsTab.tsx';
import { getDealsLabels, getRboLabels } from '@openx/components-deal/lib/DealForm/utils/dealFormUtils.ts';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import type { HistoryEntryElementMap } from '@openx/types/auditTrail.ts';
import { type Deal, dealFormField, rboConfigField } from '@openx/types/deal.ts';
import { Resource } from '@openx/types/resources.ts';
import type { FieldsValuesMappings } from '@openx/utils/history/types.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RBOSwitchFieldComponent } from './RBOSwitchFieldComponent.tsx';

interface DealHistoryTabProps {
  dealId: string;
}

export function DealHistoryTab({ dealId }: DealHistoryTabProps) {
  const { t } = useTranslation();

  const { useCurrenciesFetch, useAccountOptionsFetch, useExpectedAdCategoryFetch } = useDealContext();

  const { useCurrentInstanceState } = useTargetingContext();
  useCurrentInstanceState();
  const { loading: currenciesLoading, mapById: currencies } = useCurrenciesFetch();
  const { isExpectedAdCategoryLoading, expectedAdCategoryMap } = useExpectedAdCategoryFetch();
  const {
    accounts: { loading: accountsLoading, mapById: accounts },
  } = useAccountOptionsFetch();

  const pmpDealType = useMemo(() => ({ 3: { name: 'Within Open Auction' } }), []);

  const loading = currenciesLoading || accountsLoading || isExpectedAdCategoryLoading; /* || packages.loading */

  const labels = useMemo(() => getDealsLabels(t), [t]);
  const rboLabels = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(getRboLabels(t)).map(([key, value]) => [`${dealFormField.RBO_CONFIG}.${key}`, value])
      ),
    [t]
  );

  const groups = useMemo(
    () => [
      {
        groupTitle: t('IDs'),
        keys: [dealFormField.DEAL_ID],
      },
      {
        groupTitle: t('On Behalf Of'),
        keys: [dealFormField.ACCOUNT_UID],
      },
      {
        groupTitle: t('Deal Details'),
        keys: [
          dealFormField.NAME,
          dealFormField.PMP_DEAL_TYPE,
          dealFormField.STATUS,
          dealFormField.IMPRESSION_GOAL,
          dealFormField.DEAL_SOURCE,
          dealFormField.ACCOUNT_EXECUTIVE,
          dealFormField.ACCOUNT_MANAGER,
          dealFormField.EXPECTED_AD_CATEGORY,
        ],
      },
      {
        groupTitle: t('Deal Floor & Duration'),
        keys: [dealFormField.CURRENCY, dealFormField.DEAL_PRICE, dealFormField.START_DATE, dealFormField.END_DATE],
      },
      {
        groupTitle: t('Engagement Optimization'),
        keys: [
          ...Object.values(rboConfigField).map(field => `${dealFormField.RBO_CONFIG}.${field}`),
          dealFormField.RBO_CONFIG,
        ],
      },
      {
        groupTitle: t('Discounts'),
        keys: [dealFormField.IS_DISCOUNT_ENABLED],
      },
      {
        groupTitle: t('Targeting Criteria'),
        keys: [dealFormField.PACKAGE_UID],
      },
    ],
    [t]
  );

  const valuesMappings: FieldsValuesMappings<Deal> = useMemo(
    () => ({
      [dealFormField.ACCOUNT_UID]: (account_uid: string) => accounts[account_uid]?.name || account_uid,
      [dealFormField.CURRENCY]: (currency: string) => currencies[currency]?.name || currency,
      [dealFormField.PMP_DEAL_TYPE]: (type: string) => pmpDealType[type]?.name || type,
      [dealFormField.EXPECTED_AD_CATEGORY]: (category: string) => expectedAdCategoryMap[category]?.name ?? category,
      [dealFormField.RBO_CONFIG]: (rboConfig: string) => rboConfig,
      [dealFormField.PACKAGE_UID]: (packageUid: string) => packageUid,
    }),
    [accounts, currencies, expectedAdCategoryMap, /* packages.mapById, */ pmpDealType]
  );

  const historyEntryElementMap: HistoryEntryElementMap = {
    [dealFormField.RBO_CONFIG]: RBOSwitchFieldComponent,
  };

  const customInitialRender = useCallback(deal => <DealDetailsTab deal={deal} historyMode />, []);

  return (
    <>
      <Alert severity="warning">
        {t('Changes related to DSP, ad quality and Targeting sections are currently not available in the history log.')}
      </Alert>
      <History
        resource={Resource.DEAL}
        id={dealId}
        labels={{ ...labels, ...rboLabels }}
        valuesMappings={valuesMappings}
        groups={groups}
        isLoading={loading}
        customInitialRender={customInitialRender}
        historyEntryElementMap={historyEntryElementMap}
      />
    </>
  );
}
